/**
 * Created by Cascade on 22.01.2026
 * Community Screen - Shows all community posts
 */
import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useTrans } from '../../Store/System/LocalizationStore';
import { CommunityWallPost, CommunityWallPostApi } from '../../Model/Engagement/CommunityWall/CommunityWallPost';
import { CommunityTab } from './Components/CommunityTab';
import { LoadingContainer } from '../../Components/LoadingContainer';
import { Theme } from '../../Theme/Theme';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ScreenContainer = styled.div`
  background: #f8f9fa;
  min-height: 100vh;
  animation: ${fadeIn} 0.3s ease-out;
`;

const PageHeader = styled.div`
  background: white;
  padding: 20px 0;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};
  position: sticky;
  top: 60px;
  z-index: 10;
`;

const HeaderContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  background: ${Theme.Colors.lightestGrey};
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: ${Theme.Colors.lighterGrey};
  }
`;

const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: ${Theme.Colors.textColor};
`;

const PageContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 24px 16px 48px;
`;

const LoadMoreButton = styled.button`
  width: 100%;
  padding: 14px;
  background: white;
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.primary};
  cursor: pointer;
  margin-top: 24px;
  transition: background 0.2s;

  &:hover {
    background: ${Theme.Colors.lightestGrey};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const MotivationCommunityScreen: React.FC = observer(() => {
  const { t } = useTrans();
  const navigate = useNavigate();

  const [posts, setPosts] = useState<CommunityWallPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const loadPosts = useCallback(async (pageNum: number, append = false) => {
    if (pageNum === 0) setLoading(true);
    else setLoadingMore(true);

    try {
      const result = await CommunityWallPostApi.find({
        page: pageNum,
        size: 20,
        sort: 'createdAt,desc',
      });

      if (result) {
        setHasMore(result.length >= 20);
        setPosts((prev) => (append ? [...prev, ...result] : result));
      }
    } catch (err) {
      console.error('Error loading posts:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadPosts(0);
  }, [loadPosts]);

  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadPosts(nextPage, true);
    }
  }, [loadPosts, loadingMore, hasMore, page]);

  const handleRefresh = useCallback(() => {
    setPage(0);
    loadPosts(0);
  }, [loadPosts]);

  if (loading) {
    return <LoadingContainer text={t('motivation.loadingPosts', { defaultValue: 'Loading posts...' })} />;
  }

  return (
    <ScreenContainer>
      <PageHeader>
        <HeaderContent>
          <BackButton onClick={() => navigate(-1)}>←</BackButton>
          <HeaderTitle>💬 {t('motivation.community', { defaultValue: 'Community' })}</HeaderTitle>
        </HeaderContent>
      </PageHeader>

      <PageContent>
        <CommunityTab posts={posts} onPostsRefresh={handleRefresh} />

        {hasMore && posts.length > 0 && (
          <LoadMoreButton onClick={handleLoadMore} disabled={loadingMore}>
            {loadingMore
              ? t('motivation.loading', { defaultValue: 'Loading...' })
              : t('motivation.loadMore', { defaultValue: 'Load more' })}
          </LoadMoreButton>
        )}
      </PageContent>
    </ScreenContainer>
  );
});
